import type { PresenceDefenseSourceFoldInput } from '../types';

const SOURCE_KEYS = [
    'source_item_id', 'source_name', 'source_kind', 'source_origin_item_id',
    'source_origin_name', 'source_status_id', 'source_inventory_id', 'source_thumb_url',
] as const;
const IDENTITY_KEYS = ['source_item_id', 'source_status_id', 'source_inventory_id', 'source_kind', 'source_name'] as const;

function record(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function clone(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(clone);
    const row = record(value);
    return row ? Object.fromEntries(Object.entries(row).map(([key, entry]) => [key, clone(entry)])) : value;
}

function normalize(source: Readonly<Record<string, unknown>>): Record<string, string> {
    const result: Record<string, string> = {};
    for (const key of SOURCE_KEYS) {
        const value = source[key];
        if (value === null || value === undefined || value === '' || value === '0' || value === 0 || value === false) continue;
        if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') continue;
        result[key] = value === true ? '1' : String(value);
    }
    return result;
}

function identity(source: Readonly<Record<string, unknown>>): string {
    return IDENTITY_KEYS.map((key) => String(source[key] ?? '')).join('|');
}

/** Fold ordered source metadata into one PHP-compatible boolean presence-defense slot. */
export function foldPresenceDefenseSources(input: PresenceDefenseSourceFoldInput): unknown[] {
    const entries = input.initialEntries.map(clone);
    if (input.layerName !== 'force_move_resistance' && input.layerName !== 'immune_to_forced_movement') return entries;

    for (const source of input.sources) {
        const normalized = normalize(source);
        if (Object.keys(normalized).length === 0) continue;
        if (entries.length === 0) {
            entries.push({ item_layer_effect: '1', ...normalized, all_sources: [] });
            continue;
        }

        let head = record(entries[0]);
        if (!head) {
            head = { item_layer_effect: '1', all_sources: [] };
            entries[0] = head;
        }
        const wanted = identity(normalized);
        if (identity(head) === wanted) continue;
        const allSources = Array.isArray(head.all_sources) ? head.all_sources : [];
        if (allSources.some((existing) => {
            const row = record(existing);
            return row !== null && identity(row) === wanted;
        })) continue;
        head.all_sources = [...allSources, normalized];
    }
    return entries;
}
